import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// iter76: Customer-facing offer flow now restricts payment method to
// Stripe only. Offline methods remain in admin CRM.
const PAYMENT_OPTIONS = [
  { key: "stripe", label: "Credit/Debit Card" },
];

/**
 * MakeOfferDialog — shown when a customer wants to propose a price different
 * from the estimate. Captures price + preferred date/time + notes so an
 * accepted offer can be auto-converted into a booking.
 */
export default function MakeOfferDialog({
  open, onOpenChange,
  estimatedPrice = 0,
  serviceKey,
  squareFeet,
  frequency = "one_time",
  yardArea = "both",
  defaultCustomer = {},
  // Quote-range support — when provided, the dialog shows a range
  // and validates the offer against quoteLow * 0.85 instead of the legacy
  // 0–150% band against `estimatedPrice`.
  quoteLow = null,
  quoteHigh = null,
  quoteOptions = null,
  quoteDescription = null,
}) {
  const isRange = quoteLow !== null && quoteHigh !== null;
  const minOffer = isRange ? Math.round(quoteLow * 0.85 * 100) / 100 : 1;
  const maxOffer = isRange ? Math.round(quoteHigh * 1.5 * 100) / 100 : (estimatedPrice * 1.5);
  const navigate = useNavigate();
  const [offered, setOffered] = useState(isRange ? Math.round(quoteLow) : Math.max(15, Math.round((estimatedPrice || 0) * 0.85)));
  useEffect(() => {
    if (open) {
      const suggested = isRange ? Math.round(quoteLow) : Math.max(15, Math.round((estimatedPrice || 0) * 0.85));
      setOffered(suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, estimatedPrice, quoteLow, quoteHigh]);
  const [name, setName] = useState(defaultCustomer.name || "");
  const [email, setEmail] = useState(defaultCustomer.email || "");
  const [phone, setPhone] = useState(defaultCustomer.phone || "");
  const [address, setAddress] = useState(defaultCustomer.address || "");
  const [preferredDate, setPreferredDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [preferredTime, setPreferredTime] = useState("9:00 AM");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!serviceKey) return toast.error("Pick a service first");
    if (!name.trim()) return toast.error("Please enter your name");
    if (offered <= 0) return toast.error("Offer must be greater than $0");
    if (isRange) {
      if (offered < minOffer) return toast.error(`Offer must be at least $${minOffer.toFixed(2)} (15% below low end)`);
      if (offered > maxOffer) return toast.error(`Offer cannot exceed $${maxOffer.toFixed(2)}`);
    } else if (offered > estimatedPrice * 1.5) {
      return toast.error("Offer cannot exceed 150% of estimate");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/offers", {
        service_key: serviceKey,
        square_feet: squareFeet,
        frequency,
        yard_area: yardArea,
        estimated_price: isRange ? Math.round(((quoteLow + quoteHigh) / 2) * 100) / 100 : estimatedPrice,
        offered_price: offered,
        customer_name: name.trim(),
        customer_email: email.trim() || null,
        customer_phone: phone.trim() || null,
        address: address.trim() || null,
        preferred_date: preferredDate,
        preferred_time: preferredTime,
        payment_method: paymentMethod,
        notes: notes || null,
        quote_low: isRange ? quoteLow : null,
        quote_high: isRange ? quoteHigh : null,
        quote_options: quoteOptions,
        quote_description: quoteDescription,
      });
      toast.success("Offer sent. We'll review and respond shortly.");
      onOpenChange(false);
      navigate(`/offer/${data.offer_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit offer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="make-offer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-[#047857]" /> Make your offer</DialogTitle>
          <DialogDescription>Tell us what you'd like to pay and when you need it done. We'll respond fast.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          {isRange ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3" data-testid="offer-range-display">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-600">Estimated range</div>
                <div className="text-xl font-extrabold text-[#047857] tabular-nums">${quoteLow.toFixed(0)} – ${quoteHigh.toFixed(0)}</div>
              </div>
              {quoteDescription && <div className="mt-1 text-[11px] text-gray-600">{quoteDescription}</div>}
              <div className="mt-1 text-[10px] uppercase tracking-[0.14em] font-extrabold text-emerald-700">Offers from ${minOffer.toFixed(0)} (15% below low)</div>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 flex items-center justify-between">
              <div className="text-xs text-gray-600">Estimated price</div>
              <div className="text-xl font-extrabold text-[#047857] tabular-nums">${(estimatedPrice || 0).toFixed(2)}</div>
            </div>
          )}

          <div>
            <Label>Your offer</Label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-3xl font-extrabold text-gray-400">$</span>
              <Input
                type="number"
                min={minOffer}
                max={maxOffer}
                step="0.01"
                value={offered}
                onChange={(e) => setOffered(parseFloat(e.target.value) || 0)}
                className="text-3xl font-extrabold h-14 tabular-nums"
                data-testid="offer-price"
              />
            </div>
            {isRange ? (
              <p className="text-[11px] mt-1" data-testid="offer-range-hint">
                {offered < minOffer && <span className="text-rose-600 font-bold">Below floor — minimum ${minOffer.toFixed(0)}</span>}
                {offered >= minOffer && offered < quoteLow && <span className="text-amber-700">Below low end — admin may counter</span>}
                {offered >= quoteLow && offered <= quoteHigh && <span className="text-emerald-700 font-bold">Within range — strong offer</span>}
                {offered > quoteHigh && <span className="text-emerald-700">Above high end — likely accepted</span>}
              </p>
            ) : (
              <p className="text-[11px] text-gray-500 mt-1">
                {offered < estimatedPrice && `${Math.round(100 - (offered / estimatedPrice) * 100)}% below estimate`}
                {offered === estimatedPrice && `Matches the estimate`}
                {offered > estimatedPrice && `${Math.round((offered / estimatedPrice - 1) * 100)}% above estimate`}
              </p>
            )}
          </div>

          <div>
            <Label>Your name <span className="text-rose-500">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="offer-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="offer-email" /></div>
            <div><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="offer-phone" /></div>
          </div>
          <div><Label>Address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} /></div>

          <div className="grid grid-cols-2 gap-2">
            <div><Label>Preferred date</Label><Input type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} data-testid="offer-date" /></div>
            <div><Label>Preferred time</Label><Input value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} data-testid="offer-time" /></div>
          </div>
          <div>
            <Label>Payment method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_OPTIONS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Anything else?</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold" data-testid="offer-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send offer of $${offered.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
